
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate }      from "react-router-dom";
import useAuthStatus                   from "../../hooks/useAuthStatus";
import MobileBottomNav                 from "../../components/feature/MobileBottomNav";
import { getTransactionHistory, checkBillStatus } from "../../api/apiServices";
import html2canvas from "html2canvas";

const STATUS_CONFIG = {
  completed:  { tone: "success", icon: "ri-check-line",        text: "Completed"  },
  pending:    { tone: "warning", icon: "ri-time-line",          text: "Pending"    },
  processing: { tone: "info",    icon: "ri-loader-2-line",      text: "Processing" },
  failed:     { tone: "danger",  icon: "ri-close-circle-line",  text: "Failed"     },
  refunded:   { tone: "purple",  icon: "ri-refund-2-line",      text: "Refunded"   },
  success:    { tone: "success", icon: "ri-check-line",        text: "Completed"  },
};

const TYPE_LABELS = {
  buy:             "Crypto Purchase",
  sell:            "Crypto Sale",
  deposit:         "Crypto Deposit",
  withdraw_crypto: "Crypto Withdrawal",
  withdraw_naira:  "Naira Withdrawal",
  bill_payment:    "Bill Payment",
  airtime:         "Airtime",
  data:            "Data Bundle",
};

function normalizeTxStatus(raw) {
  const s = (raw || "").toLowerCase();
  if (s === "completed" || s === "success")    return "completed";
  if (s === "pending"   || s === "processing")  return "processing";
  if (s === "failed")                           return "failed";
  if (s === "refunded")                         return "refunded";
  return "processing";
}


function buildRawMetadataRows(meta) {
  return [];
}

function Row({ label, value, mono, highlight, warn }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex justify-between items-start py-2.5 border-b border-border gap-3">
      <span className="text-muted text-sm flex-shrink-0">{label}</span>
      <span className={`text-sm text-right break-all font-semibold ${mono ? "font-mono" : ""}`}
        style={{ color: highlight ? "var(--color-brand)" : warn ? "var(--color-warning)" : "var(--color-text-primary)" }}>
        {value}
      </span>
    </div>
  );
}

export default function TransactionDetail() {
  const { id }          = useParams();
  const navigate        = useNavigate();
  const isAuthenticated = useAuthStatus();
  const cardRef         = useRef(null);

  const [tx,      setTx]      = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveToken,   setLiveToken]  = useState(null);
  const [liveStatus,  setLiveStatus] = useState(null);
  const [checking,    setChecking]   = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { navigate("/auth"); return; }
    getTransactionHistory()
      .then(res => {
        const found = res.data?.find(t => t._id === id || t.id === id);
        setTx(found);
        if (found) {
          setLiveToken(found.metadata?.token || null);
          // FIXED — normalize on load too, so the status pill/icon
          // reflect "success"/"pending" correctly from the very first
          // render, not just after a recheck.
          setLiveStatus(normalizeTxStatus(found.status));
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id, isAuthenticated, navigate]);

  const isBillProcessing = tx?.type === "bill_payment" && liveStatus === "processing";
  const isElectricity    = tx?.asset === "Electricity";

  const recheckStatus = async () => {
    setChecking(true);
    try {
      const res = await checkBillStatus(tx._id);
      if (res.resolved) {
        // FIXED — normalize before branching (see file header).
        const resolvedStatus = normalizeTxStatus(res.txStatus);
        if (resolvedStatus === "completed") {
          setLiveStatus("completed");
          if (res.token) setLiveToken(res.token);
        } else if (resolvedStatus === "refunded") {
          setLiveStatus("refunded");
          alert("This order was refunded. Your wallet has been credited.");
        } else if (resolvedStatus === "failed") {
          setLiveStatus("failed");
        }
        // resolvedStatus === "processing" → still not resolved, fall through.
      } else {
        alert(isElectricity
          ? "Token not ready yet — try again in a few minutes."
          : "Order still processing — try again in a few minutes.");
      }
    } catch {
      alert("Could not check status. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const [shareStatus, setShareStatus] = useState("idle");

  const canvasToBlob = (canvas) =>
    new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to generate image from receipt"));
      }, "image/png");
    });

  const shareReceipt = async () => {
    if (!cardRef.current || shareStatus === "working") return;
    setShareStatus("working");

    const bg = getComputedStyle(document.documentElement).getPropertyValue("--color-surface").trim() || "#ffffff";
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: bg, scale: 2, useCORS: true, logging: false });
      const blob = await canvasToBlob(canvas);
      const file = new File([blob], "easwallet-receipt.png", { type: "image/png" });

      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ title: "Easwallet Receipt", files: [file] });
        } catch (shareErr) {
          // User cancelling the native share sheet also lands here
          // (AbortError) — that's not a real failure, just fall back
          // to a direct download instead of showing an error state
          // for something the user deliberately did.
          if (shareErr?.name !== "AbortError") {
            downloadFallback(canvas);
          } else {
            setShareStatus("idle");
            return;
          }
        }
      } else {
        downloadFallback(canvas);
      }

      setShareStatus("done");
      setTimeout(() => setShareStatus("idle"), 2500);
    } catch (err) {
      console.error("shareReceipt error:", err);
      // FIXED — was a bare text-share fallback with its own
      // .catch(() => {}) swallowing any further failure silently. Now
      // surfaces an actual error state on the button instead of
      // failing completely invisibly.
      const text = `Easwallet Receipt\n₦${Number(tx.nairaAmount).toLocaleString()}\nRef: ${tx.paystackReference || tx._id}`;
      try {
        await navigator.share?.({ title: "Easwallet Receipt", text });
        setShareStatus("done");
        setTimeout(() => setShareStatus("idle"), 2500);
      } catch {
        setShareStatus("error");
        setTimeout(() => setShareStatus("idle"), 3000);
      }
    }
  };

  const downloadFallback = (canvas) => {
    const a = document.createElement("a");
    a.download = "easwallet-receipt.png";
    a.href = canvas.toDataURL("image/png");
    a.click();
  };

  if (!isAuthenticated) return null;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="text-center">
        <i className="ri-loader-4-line animate-spin text-4xl mb-3" style={{ color: "var(--color-brand)" }} />
        <p className="text-secondary">Loading receipt…</p>
      </div>
    </div>
  );

  if (!tx) return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="text-center px-6">
        <i className="ri-error-warning-line text-5xl mb-4" style={{ color: "var(--color-danger)" }} />
        <p className="text-secondary font-medium mb-6">Transaction not found</p>
        <button onClick={() => navigate("/transactions")}
          className="px-6 py-3 bg-primary text-on-brand rounded-2xl text-sm font-semibold"
          style={{ background: "var(--color-text-primary)" }}>Back to Transactions</button>
      </div>
    </div>
  );

  const sc   = STATUS_CONFIG[liveStatus?.toLowerCase()] || STATUS_CONFIG.pending;
  const meta = tx.metadata || {};
  const units = meta.units || tx.units || null;
  const isBill = tx.type === "bill_payment";

  // FIXED — the rest of metadata.raw, auto-rendered. See buildRawMetadataRows.
  const extraRows = buildRawMetadataRows(meta);

  return (
    <div className="min-h-screen bg-bg pb-28 px-5">
      <div className="pt-12 flex items-center justify-between mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-secondary hover:text-primary">
          <i className="ri-arrow-left-line" /> Back
        </button>
        <h1 className="font-bold text-lg text-primary">Transaction Receipt</h1>
        <div className="w-14" />
      </div>

      {isBillProcessing && (
        <div className="bg-info-light border-2 border-info-border rounded-2xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin flex-shrink-0" style={{ borderColor: "var(--color-info)", borderTopColor: "transparent" }} />
            <p className="text-sm font-semibold" style={{ color: "var(--color-info-text)" }}>
              {isElectricity ? "Token being generated…" : "Order being processed…"}
            </p>
          </div>
          <p className="text-xs leading-relaxed mb-3" style={{ color: "var(--color-info)" }}>
            Your payment was received.{" "}
            {isElectricity
              ? "The electricity provider is generating your token."
              : "Your order is being processed by the provider."}
            {" "}This usually completes within a few minutes.{" "}
            <strong style={{ color: "var(--color-info-text)" }}>You do not need to pay again.</strong>
          </p>
          <button
            onClick={recheckStatus}
            disabled={checking}
            className="w-full text-xs font-semibold border px-5 py-2 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ color: "var(--color-info-text)", borderColor: "var(--color-info-border)" }}
          >
            {checking
              ? <><div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-info)", borderTopColor: "transparent" }} />Checking…</>
              : <><i className="ri-refresh-line" />{isElectricity ? "Check for Token" : "Check Order Status"}</>}
          </button>
        </div>
      )}

      {isElectricity && liveStatus === "completed" && (
        liveToken ? (
          <div className="bg-warning-light border-2 border-warning-border rounded-2xl p-4 mb-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--color-warning-text)" }}>⚡ Meter Token</p>
            <p className="font-mono font-bold text-2xl tracking-widest break-all" style={{ color: "var(--color-warning-text)" }}>{liveToken}</p>
            {units && <p className="text-sm font-bold mt-1" style={{ color: "var(--color-warning-text)" }}>{units} units</p>}
            <p className="text-xs mt-2" style={{ color: "var(--color-warning)" }}>Enter this token on your prepaid meter</p>
            <button
              onClick={() => navigator.clipboard?.writeText(liveToken).then(() => alert("Token copied!"))}
              className="mt-3 text-xs font-semibold border px-4 py-1.5 rounded-xl"
              style={{ color: "var(--color-warning-text)", borderColor: "var(--color-warning-border)" }}
            >
              <i className="ri-file-copy-line mr-1" />Copy Token
            </button>
          </div>
        ) : (
          <div className="bg-info-light border-2 border-info-border rounded-2xl p-4 mb-4 text-center">
            <i className="ri-time-line text-2xl mb-2 block" style={{ color: "var(--color-info)" }} />
            <p className="text-sm font-semibold mb-1" style={{ color: "var(--color-info-text)" }}>Token still being generated</p>
            <p className="text-xs mb-3" style={{ color: "var(--color-info)" }}>Payment confirmed. Tap below to check if your token is ready.</p>
            <button
              onClick={recheckStatus}
              disabled={checking}
              className="text-xs font-semibold border px-5 py-2 rounded-xl disabled:opacity-50 flex items-center gap-2 mx-auto"
              style={{ color: "var(--color-info-text)", borderColor: "var(--color-info-border)" }}
            >
              {checking
                ? <><div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-info)", borderTopColor: "transparent" }} />Checking…</>
                : <><i className="ri-refresh-line" />Check for Token</>}
            </button>
          </div>
        )
      )}

      <div ref={cardRef} className="bg-surface rounded-3xl shadow-lg overflow-hidden mb-4">
        <div className="relative h-6 bg-bg">
          <div className="absolute -bottom-3 left-0 right-0 h-6 bg-surface rounded-t-3xl" />
          <div className="absolute bottom-0 -left-3 w-6 h-6 bg-bg rounded-full" />
          <div className="absolute bottom-0 -right-3 w-6 h-6 bg-bg rounded-full" />
        </div>
        <div className="px-5 pb-6 pt-2">
          <div className="text-center mb-5">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: `var(--color-${sc.tone}-light)` }}>
              <i className={`${sc.icon} text-4xl`} style={{ color: `var(--color-${sc.tone})` }} />
            </div>
            <p className="font-semibold tracking-widest text-xs" style={{ color: "var(--color-brand)" }}>TRANSACTION RECEIPT</p>
            <p className="text-4xl font-bold mt-2 text-primary">₦{Number(tx.nairaAmount).toLocaleString()}</p>
            <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold px-3 py-1 rounded-full"
              style={{ background: `var(--color-${sc.tone}-light)`, color: `var(--color-${sc.tone})` }}>
              <i className={sc.icon} />{sc.text}
            </span>
          </div>

          <div className="space-y-0">
            <Row label="Type"      value={TYPE_LABELS[tx.type] || tx.type} />
            {tx.asset && tx.asset !== "NGN" && <Row label="Asset"   value={`${tx.cryptoAmount || ""} ${tx.asset}`.trim()} />}
            {tx.assetName && <Row label="Service"  value={tx.assetName} />}
            {tx.rate  && <Row label="Rate"    value={`₦${Number(tx.rate).toLocaleString()}`} />}
            {tx.fee   && <Row label="Fee"     value={`-₦${Number(tx.fee).toLocaleString()}`} />}
            {tx.bankName && (
              <><Row label="Account" value={tx.accountName} /><Row label="Bank" value={tx.bankName} /></>
            )}
            {isBill && meta.network     && <Row label="Network"    value={meta.network} />}
            {isBill && meta.disco       && <Row label="DISCO"      value={meta.disco} />}
            {isBill && meta.meterNumber && <Row label="Meter No."  value={meta.meterNumber} mono />}
            {isBill && meta.meterType   && <Row label="Meter Type" value={meta.meterType} />}
            {isBill && meta.customerName    && <Row label="Customer Name" value={meta.customerName} />}
            {isBill && meta.customerAddress && <Row label="Address"       value={meta.customerAddress} />}
            {isBill && meta.provider    && <Row label="Provider"   value={meta.provider} />}
            {isBill && meta.smartcardNo && <Row label="Smartcard"  value={meta.smartcardNo} mono />}
            {isBill && meta.variationCode && <Row label="Plan" value={meta.variationCode} mono />}
            {isBill && meta.grossAmount && meta.grossAmount !== tx.nairaAmount && (
              <Row label="Gross Amount" value={`₦${Number(meta.grossAmount).toLocaleString()}`} />
            )}
            {isBill && meta.couponDiscount > 0 && <Row label="Coupon Discount" value={`-₦${Number(meta.couponDiscount).toLocaleString()}`} highlight />}
            {isBill && meta.cashbackUsed > 0 && <Row label="Cashback Used" value={`-₦${Number(meta.cashbackUsed).toLocaleString()}`} highlight />}
            {liveToken && <Row label="Token" value={liveToken} mono highlight />}
            {units     && <Row label="Units" value={`${units} units`} highlight />}

            {/* FIXED — full provider response, auto-rendered. Previously
                everything below this point (unit cost, total cost,
                currency, package summary, electricity discount/balances,
                customer address, etc.) was silently dropped. */}
            {extraRows.map(row => (
              <Row key={row.label} label={row.label} value={row.value} />
            ))}

            <Row label="Reference" value={tx.paystackReference || meta.requestId || meta.providerRef || tx._id} mono />
            <Row label="Date"      value={new Date(tx.createdAt).toLocaleString("en-NG")} />
            <Row label="Status"    value={sc.text} />
          </div>

          
        </div>
      </div>

      {/* FIXED — button now reflects shareStatus instead of a
          permanently static label. "working" shows a spinner,
          "done" confirms success briefly, "error" surfaces a
          failure instead of looking identical to success/no-op. */}
      <button
        onClick={shareReceipt}
        disabled={shareStatus === "working"}
        className="w-full mt-5 border-2 rounded-2xl py-3 font-semibold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
        style={{
          borderColor: shareStatus === "done" ? "var(--color-success-border)" : shareStatus === "error" ? "var(--color-danger-border)" : "var(--color-border-strong)",
          color: shareStatus === "done" ? "var(--color-success-text)" : shareStatus === "error" ? "var(--color-danger-text)" : "var(--color-text-secondary)",
          background: shareStatus === "done" ? "var(--color-success-light)" : shareStatus === "error" ? "var(--color-danger-light)" : "transparent",
        }}
      >
        {shareStatus === "working" ? (
          <><div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "currentColor", borderTopColor: "transparent" }} />Preparing receipt…</>
        ) : shareStatus === "done" ? (
          <><i className="ri-checkbox-circle-line" />Receipt saved</>
        ) : shareStatus === "error" ? (
          <><i className="ri-error-warning-line" />Failed — tap to retry</>
        ) : (
          <><i className="ri-image-line" />Share Receipt as Image</>
        )}
      </button>

      <button onClick={() => navigate("/transactions")}
        className="w-full py-4 mt-4 bg-surface border border-border-strong rounded-2xl font-semibold text-secondary text-sm">
        Back to History
      </button>

      <MobileBottomNav />
    </div>
  );
}
